import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErrorApi } from '../../api/cliente';
import type { EventoDetalle, RespuestaEntrada, ValorRespuesta } from '../../api/tipos';
import { DialogoConfirmacion } from '../../componentes/DialogoConfirmacion';
import { Aviso } from '../../componentes/Estados';
import { Resultados } from '../../componentes/Resultados';
import { fechaLarga, lineaDeEvento, textoConfirmados } from '../../lib/fechas';
import { COLOR_POR_DEFECTO } from '../../lib/personas';
import { CampoPregunta, valorVacio } from './CampoPregunta';

interface Props {
  detalle: EventoDetalle;
  onCerrar: () => void;
}

type Borrador = Map<string, ValorRespuesta>;

function borradorInicial(detalle: EventoDetalle): Borrador {
  const mapa: Borrador = new Map();
  for (const valor of detalle.miRespuesta?.valores ?? []) mapa.set(valor.preguntaId, valor);
  return mapa;
}

export function ModalEvento({ detalle, onCerrar }: Props) {
  const clienteConsultas = useQueryClient();

  const [asiste, setAsiste] = useState<boolean | null>(detalle.miRespuesta?.asiste ?? null);
  const [valores, setValores] = useState<Borrador>(() => borradorInicial(detalle));
  const [sucio, setSucio] = useState(false);
  const [confirmando, setConfirmando] = useState<'guardar' | 'salir' | null>(null);

  // Con cambios sin guardar, cerrar pide confirmación en lugar de descartarlos.
  const intentarCerrar = useCallback(() => {
    if (sucio) setConfirmando('salir');
    else onCerrar();
  }, [sucio, onCerrar]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // Con el pop-up de confirmación abierto, Escape cierra solo el pop-up.
    const alEscapar = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return;
      if (confirmando) setConfirmando(null);
      else intentarCerrar();
    };
    document.addEventListener('keydown', alEscapar);
    return () => document.removeEventListener('keydown', alEscapar);
  }, [confirmando, intentarCerrar]);

  const color = detalle.tipo?.color ?? COLOR_POR_DEFECTO;
  const bloqueado = detalle.cerrado;

  // Las preguntas condicionales solo se muestran a quien confirmó que asiste.
  const visibles = useMemo(
    () => detalle.preguntas.filter((pregunta) => !pregunta.condicional || asiste === true),
    [detalle.preguntas, asiste],
  );

  const guardar = useMutation({
    mutationFn: (cuerpo: RespuestaEntrada) =>
      api.put<EventoDetalle>(`/eventos/${detalle.id}/mi-respuesta`, cuerpo),
    onSuccess: (actualizado) => {
      clienteConsultas.setQueryData(['evento', detalle.id], actualizado);
      void clienteConsultas.invalidateQueries({ queryKey: ['eventos'] });
      setSucio(false);
    },
  });

  // Quien confirma que asiste tiene que responder todas las preguntas.
  // El backend aplica la misma regla.
  const sinResponder = new Set(
    asiste === true
      ? visibles
          .filter((pregunta) => !valores.get(pregunta.id)?.opcionIds.length)
          .map((pregunta) => pregunta.id)
      : [],
  );

  const puedeGuardar =
    asiste !== null && sucio && sinResponder.size === 0 && !bloqueado && !guardar.isPending;
  const errorGuardar = guardar.error instanceof ErrorApi ? guardar.error : null;

  const cambiarValor = (valor: ValorRespuesta) => {
    setValores((previo) => new Map(previo).set(valor.preguntaId, valor));
    setSucio(true);
  };

  const enviar = () => {
    setConfirmando(null);
    if (asiste === null) return;
    guardar.mutate({
      asiste,
      valores: visibles.map((pregunta) => valores.get(pregunta.id) ?? valorVacio(pregunta.id)),
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto bg-[rgba(8,15,26,.72)] px-4 py-8 sm:px-5 sm:py-12"
      onClick={intentarCerrar}
      role="presentation"
    >
      <div
        className="mx-auto w-full max-w-[620px] animate-aparecer overflow-hidden rounded-2xl border border-white/[.12] bg-panel shadow-modal"
        onClick={(evento) => evento.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={detalle.titulo}
      >
        <div className="h-1" style={{ background: color }} />

        <div className="flex items-start gap-3.5 px-5 pt-5 sm:px-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-titulo text-[20px] font-extrabold sm:text-[21px]">
                {detalle.titulo}
              </h2>
              {detalle.tipo && (
                <span
                  className="rounded-[5px] px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[.7px] text-tinta"
                  style={{ background: color }}
                >
                  {detalle.tipo.nombre}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-[12.5px] text-debil">
              {lineaDeEvento(detalle.fechaLocal, detalle.horaLocal, detalle.lugar)} ·{' '}
              {textoConfirmados(detalle.confirmados)}
            </p>
          </div>

          <button
            type="button"
            onClick={intentarCerrar}
            aria-label="Cerrar"
            className="h-8 w-8 flex-none rounded-[9px] border border-white/15 text-sm leading-none text-tenue transition-colors hover:bg-white/5"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col gap-5 px-5 pb-6 pt-4 sm:px-6">
          {detalle.descripcion && (
            <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-cuerpo">
              {detalle.descripcion}
            </p>
          )}

          {bloqueado && (
            <Aviso tono="aviso">
              La inscripción a este evento ya está cerrada, no se puede modificar la respuesta.
            </Aviso>
          )}

          <div>
            <div className="mb-2.5 text-[11px] uppercase tracking-[1.3px] text-debil">
              ¿Vas a asistir?
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                disabled={bloqueado}
                onClick={() => {
                  setAsiste(true);
                  setSucio(true);
                }}
                className={`rounded-[10px] px-5 py-2.5 font-titulo text-sm font-semibold transition-colors disabled:cursor-default disabled:opacity-50 ${
                  asiste === true
                    ? 'border border-lima bg-lima text-tinta'
                    : 'border border-white/[.18] text-suave hover:border-white/30'
                }`}
              >
                Sí, voy
              </button>
              <button
                type="button"
                disabled={bloqueado}
                onClick={() => {
                  setAsiste(false);
                  setValores(new Map());
                  setSucio(true);
                }}
                className={`rounded-[10px] px-5 py-2.5 font-titulo text-sm font-semibold transition-colors disabled:cursor-default disabled:opacity-50 ${
                  asiste === false
                    ? 'border border-tenue bg-tenue text-tinta'
                    : 'border border-white/[.18] text-suave hover:border-white/30'
                }`}
              >
                No puedo
              </button>
            </div>
          </div>

          {visibles.map((pregunta) => (
            <div key={pregunta.id} className="border-t border-dashed border-white/10 pt-4">
              <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
                <span className="font-titulo text-[15px] font-bold">{pregunta.etiqueta}</span>
                {pregunta.condicional && (
                  <span className="rounded border border-lima/40 px-1.5 py-px text-[10px] uppercase tracking-[.6px] text-lima">
                    Solo si asistís
                  </span>
                )}
                {pregunta.privada && (
                  <span className="rounded border border-cian/40 px-1.5 py-px text-[10px] uppercase tracking-[.6px] text-cian">
                    Solo lo ve la organización
                  </span>
                )}
                {sinResponder.has(pregunta.id) && !bloqueado && (
                  <span className="rounded border border-coral/50 px-1.5 py-px text-[10px] uppercase tracking-[.6px] text-coral">
                    Obligatoria
                  </span>
                )}
              </div>

              <CampoPregunta
                pregunta={pregunta}
                valor={valores.get(pregunta.id) ?? valorVacio(pregunta.id)}
                deshabilitado={bloqueado}
                onCambio={cambiarValor}
              />
            </div>
          ))}

          {errorGuardar && <Aviso tono="error">{errorGuardar.message}</Aviso>}

          {!bloqueado && (
            <div className="flex flex-wrap items-center gap-3.5 pt-1">
              <button
                type="button"
                disabled={!puedeGuardar}
                onClick={() => setConfirmando('guardar')}
                className={`rounded-[10px] px-6 py-2.5 font-titulo text-sm font-bold transition-colors ${
                  puedeGuardar
                    ? 'bg-lima text-tinta hover:bg-lima-claro'
                    : 'cursor-default bg-white/[.07] text-mudo'
                }`}
              >
                {guardar.isPending
                  ? 'Guardando…'
                  : detalle.miRespuesta
                    ? 'Actualizar respuesta'
                    : 'Enviar respuesta'}
              </button>

              <span className="text-[12.5px] text-mudo">
                {asiste === null
                  ? 'Elegí si vas o no para poder enviar.'
                  : sinResponder.size > 0
                    ? `Para confirmar tu asistencia respondé todas las preguntas (${
                        sinResponder.size === 1 ? 'falta 1' : `faltan ${sinResponder.size}`
                      }).`
                    : sucio
                    ? 'Tenés cambios sin guardar.'
                    : detalle.cierreLocal
                      ? `Respuesta guardada. Podés cambiarla hasta el ${fechaLarga(detalle.cierreLocal)}${
                          detalle.cierreHoraLocal && detalle.cierreHoraLocal !== '23:59'
                            ? ` a las ${detalle.cierreHoraLocal}`
                            : ''
                        }.`
                      : 'Respuesta guardada.'}
              </span>
            </div>
          )}

          {detalle.resultados.length > 0 && (
            <div className="border-t border-white/[.08] pt-4">
              <div className="mb-3 text-[11px] uppercase tracking-[1.3px] text-debil">
                Resultados hasta ahora
              </div>
              <Resultados resultados={detalle.resultados} compacto />
            </div>
          )}

        </div>
      </div>

      {confirmando === 'guardar' && (
        <DialogoConfirmacion
          titulo={detalle.miRespuesta ? '¿Actualizar tu respuesta?' : '¿Enviar tu respuesta?'}
          textoConfirmar={detalle.miRespuesta ? 'Sí, actualizar' : 'Sí, enviar'}
          onConfirmar={enviar}
          onCancelar={() => setConfirmando(null)}
        >
          {asiste ? 'Vas a confirmar que asistís' : 'Vas a avisar que no asistís'} a «
          {detalle.titulo}».
        </DialogoConfirmacion>
      )}

      {confirmando === 'salir' && (
        <DialogoConfirmacion
          titulo="¿Salir sin guardar?"
          textoConfirmar="Salir sin guardar"
          textoCancelar="Seguir editando"
          tono="peligro"
          onConfirmar={onCerrar}
          onCancelar={() => setConfirmando(null)}
        >
          Tenés cambios en tu respuesta que todavía no guardaste. Si salís, se pierden.
        </DialogoConfirmacion>
      )}
    </div>
  );
}
